//import necessary modules and components
import React, { useState, useEffect, useContext, Fragment } from "react";
import Loader from "../../Shared/Loader/loader";
import { MsalContext } from "@azure/msal-react";
import { PageContainer, Content, Header } from "../../Drawer/PageContainer";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Scrollable from "../../Shared/Edit/Scrollable";
import UserDisplay from "../../Shared/User/UserDisplay";
import { InviteOrganizationUsersDailog } from "./InviteOrganizationUsers";
import InnerLoader from "../../Shared/Loader/InnerLoader";
import RemoveDialog from "../../Shared/Remove/RemoveDialog";
import Upgarde from "../../Shared/Subscription/Upgrade";
import { NotificationGroup, Notification } from '@progress/kendo-react-notification';
import { OverlayLoader } from "../../Shared/Loader/OverlayLoader";
import { sendEmail } from "../../../API/EmailApi";
import { getAzureUser } from "../../../API/User/User";
import { getOrganizationUsers, removeUserFromOrganization } from "../../../API/Organization/organization";
import { useTranslation } from "react-i18next";
import { assignSubscriptionToUser, removeSubscriptionFromUser } from "../../../API/Subscription/SubscriptionHelper";
import { removeUserFromOrganizationOnRuntime } from "../../../API/Runtime/RuntimeHelper";

// Define the OrganizationUsers component and accept the organization, user props
const OrganizationUsers = ({
    organization,
    user
}) => {
    //context
    const context = useContext(MsalContext); // Get MsalContext
    const [loading, setLoading] = useState(true); // Define the loading state and set it to true
    const [updatingUser, setUpdatingUser] = useState(false); // Define the updatingUser state and set it to false
    const [selectedUser, setSelectedUser] = useState(null); // Define the selectedUser state and set it to null
    const [roles, setRoles] = useState([]); // Define the roles state and set it to an empty array
    const [organizationUsers, setOrganizationUsers] = useState([]); // Define the organizationUsers state and set it to an empty array
    const [listOrganizationUsers, setListOrganizationUsers] = useState([]); // Define the listOrganizationUsers state and set it to an empty array

    const [showInviteUserDialog, setShowInviteUserDialog] = useState(false); // Define the showInviteUserDialog state and set it to false
    const [showDeleteDialog, setShowDeleteDialog] = useState(false); // Define the showDeleteDialog state and set it to false
    const [showRemoveLicenseDialog, setShowRemoveLicenseDialog] = useState(false); // Define the showRemoveLicenseDialog state and set it to false
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false); // Define the showUpgradeDialog state and set it to false
    const [showNotification, setShowNotification] = useState(false); // Define the showNotification state and set it to false
    const [notificationMessage, setNotificationMessage] = useState(""); // Define the notificationMessage state and set it to an empty string

    const { t, i18n } = useTranslation([
        "users/common",
        "users/remove_user_dialog",
        "users/remove_subscription_dialog",
    ], {
        useSuspense: false
    }) // Translation

    // Define an useEffect hook that is called when the organizationData
    useEffect(() => {
        // Define an asynchronous function that fetches the necessary data
        const getOrganizationUsersData = async () => {
            // try catch block to catch any errors
            try {
                const organizationUsers = await getOrganizationUsers(organization._id);
                const roles = getOrganizationRoles();
                setRoles(roles);
                setOrganizationUsers(arrangeUsers(organizationUsers));
                setListOrganizationUsers(arrangeUsers(organizationUsers));
            } catch (error) {
                console.log(error);
            } finally {
                // Set the loading state to false
                setLoading(false);
            }
        };
        // Call the getOrganizationUsersData function
        getOrganizationUsersData();
    }, [organization]);

    const getOrganizationRoles = () => {
        let { roles } = organization;
        const filteredRoles = roles.filter((role) => role.roleType !== 'Owner');
        const sortedRoles = filteredRoles.sort((a, b) => a.order - b.order);
        return sortedRoles;
    };

    // Define a function that returns the number of subscription users
    const getNumberofSubscriptionUser = () => {
        const subscriptionUsers = organizationUsers.filter(user => user.isSubscriptionUser);
        return subscriptionUsers.length || 0;
    }

    // Define a function that arranges the users based on their role
    const arrangeUsers = (users) => {
        // Define an array of roleTypes
        const roleTypes = ["Owner", "Administrator", "Member", "App Participant", "App Guest"];
        let arrangedUsers = [];

        // Loop through the roleTypes array and filter the users based on their roleType
        roleTypes.forEach(roleType => {
            const filteredUsers = users.filter(user => user.roleType === roleType);
            // Loop through the filtered users and add them to the arrangedUsers array
            filteredUsers.forEach(user => {
                user.indexId = arrangedUsers.length + 1;
                arrangedUsers.push(user);
            });
        });
        // Return the arrangedUsers array
        return arrangedUsers;
    }

    //define a function that filters the users based on the search value
    const filterUsers = (e) => {
        let users = [...listOrganizationUsers];
        const searchValue = e.target.value.trim().toLowerCase();
        if (searchValue !== "") {
            users = users.filter(user =>
                user.displayName.toLowerCase().includes(searchValue) ||
                user.email.toLowerCase().includes(searchValue)
            );
        }
        setOrganizationUsers(arrangeUsers(users));
    };

    // handle notification with settimeout
    const handleNotification = (message) => {
        setShowNotification(true);
        setNotificationMessage(message);
        setTimeout(() => {
            setShowNotification(false);
            setNotificationMessage("");
        }, 2000); // set timeout to 2 seconds
    }

    // define a function that handle edit user
    const handleEditUser = (user) => {
        setSelectedUser(user); // Set the selectedUser state to the user
        setShowInviteUserDialog(true); // Set the showInviteUserDialog state to true
    }

    // define a function that handle delete user
    const handleDeleteUser = (user) => {
        setSelectedUser(user);  // Set the selectedUser state to the user
        setShowDeleteDialog(true); // Set the showDeleteDialog state to true
    }

    // define a function that remove license from user
    const handleRemoveLicenseforUser = (user) => {
        setSelectedUser(user);  // Set the selectedUser state to the user
        setShowRemoveLicenseDialog(true); // Set the showRemoveLicenseDialog state to true
    }

    // define a function that handle update organization users
    const setUsers = (users) => {
        const arrangedUsers = arrangeUsers(users); // Call the arrangeUsers function and pass in the users
        setOrganizationUsers(arrangedUsers); // Set the organizationUsers state to the arrangedUsers
        setListOrganizationUsers(arrangedUsers); // Set the listOrganizationUsers state to the arrangedUsers
    }

    // Delete user from organization
    const deleteUser = async (event) => {
        event.preventDefault();
        // Set the updatingUser state to true
        setUpdatingUser(true);
        try {
            // Call the removeUserFromOrganization API
            await Promise.all([
                removeUserFromOrganization(organization._id, selectedUser._id),
                removeUserFromOrganizationOnRuntime(organization._id, selectedUser.azureId, selectedUser.email)
            ])
            const updatedUsers = listOrganizationUsers.filter(user => user.email !== selectedUser.email); // Filter out the selected user from the listOrganizationUsers state
            setUsers(updatedUsers); // Set the users
        } catch (err) {
            console.log(err); // Log an error message
        } finally {
            setUpdatingUser(false); // Set the updatingUser state to false
            setSelectedUser(null); // Set the selectedUser state to null
            setShowDeleteDialog(false); // Set the showDeleteDialog state to false
        }
    }

    // Define a funtion that handles the remove license from user
    const removeLicenseForUser = async (event) => {
        event.preventDefault();
        // Set the updatingUser state to true
        setUpdatingUser(true);
        try {
            // Call the removeUserLicense API
            await removeSubscriptionFromUser(organization._id, selectedUser._id)

            let index = organizationUsers.findIndex(dataItem => dataItem._id === selectedUser._id)
            if (index > -1) {
                organizationUsers[index].isSubscriptionUser = false
            }
            setOrganizationUsers(organizationUsers); // Set the organizationUsers state to the updated users
        } catch (err) {
            console.log(err); // Log an error message
        } finally {
            setUpdatingUser(false); // Set the updatingUser state to false
            setSelectedUser(null); // Set the selectedUser state to null
            setShowRemoveLicenseDialog(false); // Set the showDeleteDialog state to false
        }
    }

    // Define a funtion that handles the assign license from user
    const assignLicenseForUser = async (user) => {
        // Set the updatingUser state to true
        setUpdatingUser(true);
        setSelectedUser(user);  // Set the selectedUser state to the user
        try {
            // Call the assignUserLicense API
            const response = await assignSubscriptionToUser(organization._id, user._id)
            if (response.status && response.status === "Failure") {
                setShowUpgradeDialog(true); // Set the showUpgradeDailog state to true
            } else {
                const message = "User " + user.email + " has been assigned subscription"
                handleNotification(message)
                let index = organizationUsers.findIndex(dataItem => dataItem._id === user._id)
                if (index > -1) {
                    organizationUsers[index].isSubscriptionUser = true
                }
                setOrganizationUsers(organizationUsers); // Set the organizationUsers state to the updated users
            }
        } catch (err) {
            console.log(err); // Log an error message
        } finally {
            setUpdatingUser(false); // Set the updatingUser state to false
            setSelectedUser(null); // Set the selectedUser state to null
        }
    }

    // Define a function that resend email to user
    const resendInvitation = async (user) => {
        let data = [...organizationUsers]
        let index = data.findIndex(dataItem => dataItem._id === user._id)
        data[index].sendingEmail = true

        const userAzureData = await getAzureUser(context)

        let properties = {
            userName: userAzureData.name,
            organizationName: organization.orgName,
            workspaceUrl: `${process.env.REACT_APP_RUNTIME_URL}/${organization.orgUrl}`,
            subject: "You have been invited to CodelessONE"
        };

        // generate workspace url based on user's azure id
        if (user.azureId && user.azureId !== "") {
            properties.designerUrl = properties.workspaceUrl
        } else {
            properties.designerUrl = `${process.env.REACT_APP_DESIGNER_URL}/signup`
        }

        await sendEmail(user.email, process.env.REACT_APP_SENDGRID_INVITE_KEY, properties);
        setOrganizationUsers(data);
        setTimeout(() => {
            data[index].sendingEmail = false
            setOrganizationUsers(data);
        }, 5000);
    };

    // If inProgress is not "none", show a Loader component
    if (context.inProgress !== "none" || loading) {
        return <Loader />;
    }

    // Otherwise, show the OrganizationUsers component
    return (
        <PageContainer>
            <Header
                hideBorder={true}
                actionText={t("header.buttons.invite_user_button_label")}
                showAction={true}
                actionClicked={() => setShowInviteUserDialog(true)} >
                <Fragment>
                    {/* Organization seats available */}
                    <span className="me-4">{t("header.number_of_seat_assigned")} {getNumberofSubscriptionUser()} \ {organization.numSeats}</span>

                    {/* Search Organization Users */}
                    <input
                        style={{ width: 250 }}
                        type="text"
                        onChange={filterUsers}
                        placeholder={t("header.search_user_placeholder")}
                        className="form-control unicode mx-1"
                    />
                </Fragment>
            </Header>
            <Content>
                <Scrollable>
                    {/* Show the OverlayLoader component if updatingUser is true */}
                    {updatingUser && <OverlayLoader />}

                    {/* Show notification for invite user, assign user licence, remove user licence */}
                    {showNotification &&
                        <NotificationGroup style={{ top: 0, left: "50%", transform: "translateX(-50%)" }}>
                            <Notification
                                key="success"
                                closable={true}
                                type={{
                                    style: "success",
                                    icon: true,
                                }}
                            >
                                {notificationMessage}
                            </Notification>
                        </NotificationGroup>
                    }

                    {/* Invite User to Organization Dialog */}
                    {showInviteUserDialog &&
                        <InviteOrganizationUsersDailog
                            organization={organization}
                            orgRole={roles}
                            organizationUsers={listOrganizationUsers}
                            showInIframe={false}
                            toUpdateUser={selectedUser}
                            updateUsers={setUsers}
                            showNotification={handleNotification}
                            closeDialog={() => {
                                setSelectedUser(null)
                                setShowInviteUserDialog(false)
                            }}
                        />
                    }

                    {/* Upgrade Subscription Dialog */}
                    {showUpgradeDialog &&
                        <Upgarde
                            organization={organization}
                            Headline={'Need more subscription ?'}
                            toggleDialog={() => setShowUpgradeDialog(false)}
                            Type={'UserSeats'}
                        />
                    }

                    {/* Remove User From Organization Dialog */}
                    {showDeleteDialog && (
                        <RemoveDialog
                            toggleDeleteDialog={() => {
                                setSelectedUser(null)
                                setShowDeleteDialog(false)
                            }}
                            showLoader={updatingUser}
                            message={t("warning", { email: selectedUser.email, ns: "users/remove_user_dialog" })}
                            onDelete={deleteUser}
                            Title={t("title", { ns: "users/remove_user_dialog" })}
                            deleteButtonText={t("buttons.remove_button_label", { ns: "users/remove_user_dialog" })}
                            cancelButtonText={t("buttons.cancel_button_label", { ns: "users/remove_user_dialog" })}
                            Reset={false}
                        />
                    )}

                    {/* Remove User License Dialog */}
                    {showRemoveLicenseDialog && (
                        <RemoveDialog
                            toggleDeleteDialog={() => {
                                setSelectedUser(null)
                                setShowRemoveLicenseDialog(false)
                            }}
                            showLoader={updatingUser}
                            message={t("warning", { email: selectedUser.email, ns: "users/remove_subscription_dialog" })}
                            onDelete={removeLicenseForUser}
                            Title={t("title", { ns: "users/remove_subscription_dialog" })}
                            deleteButtonText={t("buttons.remove_button_label", { ns: "users/remove_subscription_dialog" })}
                            cancelButtonText={t("buttons.cancel_button_label", { ns: "users/remove_subscription_dialog" })}
                            Reset={false}
                        />
                    )}

                    {/* Organization Users Grid */}
                    <div className="m-9 my-3 checkRuntimeUser">
                        <Grid
                            reorderable={true}
                            data={organizationUsers}
                            style={{ height: "100%" }}
                        >
                            <Column width={40} title="#" field="indexId" />
                            <Column width={400} title={t("grid.header.user")} field="email" cell={(props) => {
                                return (
                                    <td>
                                        <UserDisplay {...props.dataItem} showEmail={!!props.dataItem.firstName} />
                                    </td>
                                );
                            }} />
                            <Column title={t("grid.header.workspace_role")} cell={(props) => {
                                return (
                                    <td>
                                        <div className="title-xs">
                                            {t("grid.role_type." + props.dataItem.roleType.toLowerCase().replace(" ", "_"))}
                                        </div>
                                    </td>
                                );
                            }} />
                            <Column title={t("grid.header.status")} cell={(props) => {
                                return (
                                    <td>
                                        <span className={props.dataItem.invitationAccepted === "Accepted" ? "" : "text-danger"}>
                                            {
                                                props.dataItem.invitationAccepted === "Accepted" ?
                                                    t("grid.status_type.joined")
                                                    :
                                                    t("grid.status_type.pending")
                                            }
                                        </span>
                                        <span className="ps-2">
                                            {props.dataItem.invitationAccepted !== "Accepted" && (
                                                <>
                                                    {!props.dataItem.sendingEmail &&
                                                        <span className="txt-link pointer" onClick={() => resendInvitation(props.dataItem)}>
                                                            {t("grid.resent_invitation")}
                                                        </span>
                                                    }
                                                    {props.dataItem.sendingEmail &&
                                                        <span className="text-success">
                                                            {t("grid.invitation_sent")}
                                                        </span>
                                                    }
                                                </>
                                            )}
                                        </span>
                                    </td>
                                );
                            }}
                            />
                            <Column title={t("grid.header.access_level")} cell={(props) => {
                                return (
                                    <td>
                                        <span className="me-2">
                                            {props.dataItem.isSubscriptionUser ?
                                                t("grid.subscription_user")
                                                :
                                                t("grid.non_subscription_user")
                                            }
                                        </span>
                                    </td>
                                )
                            }} />
                            <Column width={180} title={t("grid.header.actions")} cell={(props) => {
                                return (
                                    <td>
                                        {(props.dataItem.roleType === "Owner" || props.dataItem.email === user.email) ? null :
                                            <Fragment>
                                                <button
                                                    title={t("grid.action_button_title.update_user")}
                                                    onClick={() => handleEditUser(props.dataItem)}
                                                    className="me-1 btn btn-link"
                                                >
                                                    <i className="fa fa-pencil text-secondary"></i>
                                                </button>
                                                <button
                                                    disabled={!props.dataItem.azureId || updatingUser || showNotification}
                                                    title={props.dataItem.isSubscriptionUser ?
                                                        t("grid.action_button_title.remove_license")
                                                        :
                                                        t("grid.action_button_title.assign_license")
                                                    }
                                                    className="me-1 btn btn-link btn-lg"
                                                    onClick={() => props.dataItem.isSubscriptionUser ? handleRemoveLicenseforUser(props.dataItem) : assignLicenseForUser(props.dataItem)}
                                                    type={'button'}>
                                                    {props.dataItem.isSubscriptionUser ? <i class="far fa-minus-circle"></i> : <i class="far fa-plus-circle"></i>}
                                                </button>
                                                <button
                                                    title={t("grid.action_button_title.delete_user")}
                                                    onClick={() => handleDeleteUser(props.dataItem)}
                                                    className="me-1 btn btn-link"
                                                >
                                                    <i className="fa fa-trash-alt text-danger"></i>
                                                </button>

                                                <small className="mt-1">
                                                    {(updatingUser && selectedUser._id === props.dataItem._id) && (
                                                        <div className="d-inline-block">
                                                            <InnerLoader borderColor="green" />
                                                            <span className="me-4 text-success"></span>
                                                        </div>
                                                    )}
                                                </small>
                                            </Fragment>
                                        }
                                    </td>
                                );
                            }} />
                        </Grid>
                    </div>
                </Scrollable>
            </Content>
        </PageContainer>
    )
}

export default OrganizationUsers